import { setCurrentUser, setName, setUserId, setAccId } from '../components/login/loginSlice';

export function hasMultipleAccounts(users) {
    return Array.isArray(users) && users.length > 1;
}

/** Компания выбрана в режиме делегированного доступа (не владелец). */
export function isDelegatedCompanyAccess(user) {
    if (!user) return false;
    return user.access_mode === 'delegated';
}

/**
 * Id личного аккаунта (не company-context).
 * После выбора компании session userId часто = legacy / company.id (46),
 * а настоящий бизнес-аккаунт лежит в currentUser.business_user_id (131).
 * Вне контекста компании сессия важнее первого элемента userList.
 *
 * Для delegated: business_user_id = гость (employee), не владелец компании.
 */
export function resolveBusinessAccountId({ users, currentUser, userId } = {}) {
    const sessionId = Number(userId) || 0;
    const inCompanyContext = !!(currentUser?.is_company_context || currentUser?.is_company_record);

    if (inCompanyContext) {
        if (isDelegatedCompanyAccess(currentUser)) {
            const guest =
                Number(currentUser?.employee_user_id) ||
                Number(currentUser?.business_user_id) ||
                0;
            if (guest > 0) return guest;
        }
        const biz = Number(currentUser?.business_user_id) || 0;
        if (biz > 0) return biz;
        const loginRow = (users || []).find((u) => u && !u.is_company_context && !u.is_company_record);
        if (loginRow?.user_id) return Number(loginRow.user_id);
        if (sessionId) return sessionId;
        return null;
    }

    if (sessionId) return sessionId;

    if (currentUser?.id && !currentUser?.is_company_context && !currentUser?.is_company_record) {
        return Number(currentUser.id);
    }

    const loginRow = (users || []).find((u) => u && !u.is_company_context && !u.is_company_record);
    if (loginRow?.user_id) return Number(loginRow.user_id);

    if (currentUser?.business_user_id) return Number(currentUser.business_user_id);

    return null;
}

/**
 * Владелец данных компании (пакеты, задачи) — для delegated это company_owner_user_id,
 * для owner — business-аккаунт сессии.
 */
export function resolveCompanyOwnerUserId({ users, currentUser, userId, businessAccountId } = {}) {
    if (isDelegatedCompanyAccess(currentUser)) {
        const owner = Number(currentUser?.company_owner_user_id) || 0;
        if (owner > 0) return owner;
    }
    const biz =
        Number(businessAccountId) ||
        resolveBusinessAccountId({ users, currentUser, userId }) ||
        0;
    if (biz > 0) return biz;
    if (currentUser?.is_company_context || currentUser?.is_company_record) {
        const fallback =
            Number(currentUser?.company_owner_user_id) ||
            Number(currentUser?.business_user_id) ||
            0;
        if (fallback > 0) return fallback;
    }
    return Number(userId) || null;
}

export function applyAccountSwitch(dispatch, user) {
    if (!user) return;
    dispatch(setName(user.company_name));
    dispatch(setUserId(user.user_id));
    if (user.accountant_id != null && Number(user.accountant_id) > 0) {
        dispatch(setAccId(Number(user.accountant_id)));
    }
    dispatch(setCurrentUser({
        ...user,
        id: user.user_id,
        name: user.company_name,
        public_id: user.public_id || null,
        employee_user_id: user.employee_user_id || null,
        access_mode: user.access_mode || 'owner',
        is_company_context: user.is_company_context != null
            ? !!user.is_company_context
            : !!(user.is_company_record || user.company_id),
        is_company_record: !!user.is_company_record,
        company_id: user.company_id || null,
        legacy_user_id: user.legacy_user_id || null,
        has_legacy_user: !!user.has_legacy_user || !!user.legacy_user_id,
        accountant_id: user.accountant_id || null,
        business_user_id: user.business_user_id || null,
        company_owner_user_id: user.company_owner_user_id || null,
        access_documents: user.access_documents,
        access_invoices: user.access_invoices,
        access_bank: user.access_bank,
        access_tax: user.access_tax,
        access_salary: user.access_salary,
        access_buh_bill: user.access_buh_bill,
        // Аватар/имя владельца аккаунта (профиль) — для шапки сообщений компаний
        owner_photo_path: user.owner_photo_path || null,
        owner_name: user.owner_name || null,
    }));
}

/** @deprecated не используем account_type; оставлено для совместимости */
export function isEmployeeAccount(userOrType) {
    if (!userOrType) return false;
    if (typeof userOrType === 'string') return userOrType === 'employee';
    return userOrType.account_type === 'employee';
}
